/**
 * USERS MANAGEMENT PAGE
 *
 * System admins and team admins can manage users and assign them roles.
 * SYSTEM_ADMIN: Can manage all users and assign any role
 * IT_ADMIN: Can manage IT_TEAM members only
 * DATA_ADMIN: Can manage DATA_TEAM members only
 * PLATFORM_ADMIN: Can manage PLATFORM_TEAM members only
 */

import { useAuth } from '@/hooks/use-auth';
import { useNavigate } from 'react-router-dom';
import type { UserRole } from '@/types/database';
import { isAdmin } from '@/lib/role-helpers';
import { UserManagementForm } from '@/components/user-management-form';

export default function UsersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Loading state
  if (authLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto'></div>
          <p className='mt-4 text-gray-600 dark:text-gray-400'>Loading...</p>
        </div>
      </div>
    );
  }

  // Unauthorized check - only admins can access this page
  if (!user || !isAdmin(user.role)) {
    navigate('/unauthorized');
    return null;
  }

  // Determine what this user can manage based on their role
  let allowedRoles: UserRole[] = [];
  let pageTitle = 'Team Members';
  let pageDescription = 'Create and manage team members';
  let filterByTeam: 'PLATFORM' | 'IT' | 'DATA' | null = null;

  switch (user.role) {
    case 'SYSTEM_ADMIN':
      // System admin can see all users and assign any role
      allowedRoles = ['SYSTEM_ADMIN', 'PLATFORM_TEAM', 'PLATFORM_ADMIN', 'IT_TEAM', 'IT_ADMIN', 'DATA_TEAM', 'DATA_ADMIN'];
      pageTitle = 'Team Members';
      pageDescription = 'Create and manage team members across all roles';
      filterByTeam = null;
      break;
    case 'IT_ADMIN':
      // IT admin can manage IT_TEAM members and assign them IT_ADMIN role
      allowedRoles = ['IT_TEAM', 'IT_ADMIN'];
      pageTitle = 'IT Team Members';
      pageDescription = 'Manage IT team members';
      filterByTeam = 'IT';
      break;
    case 'DATA_ADMIN':
      // Data admin can manage DATA_TEAM members and assign them DATA_ADMIN role
      allowedRoles = ['DATA_TEAM', 'DATA_ADMIN'];
      pageTitle = 'Data Team Members';
      pageDescription = 'Manage Data team members';
      filterByTeam = 'DATA';
      break;
    case 'PLATFORM_ADMIN':
      // Platform admin can manage PLATFORM_TEAM members and assign them PLATFORM_ADMIN role
      allowedRoles = ['PLATFORM_TEAM', 'PLATFORM_ADMIN'];
      pageTitle = 'Platform Team Members';
      pageDescription = 'Manage Platform team members';
      filterByTeam = 'PLATFORM';
      break;
    default:
      // This shouldn't happen as isAdmin checks above
      navigate('/unauthorized');
      return null;
  }

  return (
    <div className='w-full'>
      <UserManagementForm
        allowedRoles={allowedRoles}
        filterByTeam={filterByTeam}
        showSearch={true}
        showRoleFilter={user.role === 'SYSTEM_ADMIN'}
        title={pageTitle}
        description={pageDescription}
        currentUserId={user.id}
      />
    </div>
  );
}
